import type { EngagementAutomation, EngagementMessage, EngagementSettings } from 'types/engagement.types';

/**
 * Whether the two global switches (ENGAGEMENT_SEND_ENABLED / ENGAGEMENT_AUTOMATIONS_ENABLED) are
 * off. The API states them (GET /settings: top-level and under `flags`; GET /automations:
 * `automationsEnabled`), so:
 *  1. an explicit flag from the API wins (`settings.flags` or the automations response),
 *  2. otherwise it is derived from what the API reports: `skipped_disabled` deliveries mean real
 *     sends were swallowed; automations that would send but sent nothing mean automations are off.
 * `undefined` = unknown (no evidence either way) — then no banner is shown.
 */

export const countSkippedDisabled = (messages: EngagementMessage[]): number =>
  messages.reduce((sum, m) => {
    const skipped = m.aggregate?.skipped ?? {};
    return sum + (skipped.skipped_disabled ?? 0) + (skipped.disabled ?? 0);
  }, 0);

export const deriveSendEnabled = (settings?: EngagementSettings, messages: EngagementMessage[] = []): boolean | undefined => {
  if (typeof settings?.flags?.sendEnabled === 'boolean') return settings.flags.sendEnabled;
  if (typeof settings?.sendEnabled === 'boolean') return settings.sendEnabled;
  if (countSkippedDisabled(messages) > 0) return false;
  return undefined;
};

export interface AutomationsResponse {
  automations: EngagementAutomation[];
  automationsEnabled?: boolean;
}

/** GET /automations answers `{ automationsEnabled, items }` (an array or `automations` is still accepted). */
export const normalizeAutomations = (data: unknown): AutomationsResponse => {
  if (Array.isArray(data)) return { automations: data as EngagementAutomation[] };
  const obj = (data ?? {}) as Record<string, unknown>;
  const list = (Array.isArray(obj.automations) ? obj.automations : Array.isArray(obj.items) ? obj.items : []) as EngagementAutomation[];
  const flags = obj.flags as { automationsEnabled?: boolean } | undefined;
  const enabled =
    typeof obj.automationsEnabled === 'boolean'
      ? obj.automationsEnabled
      : typeof flags?.automationsEnabled === 'boolean'
        ? flags.automationsEnabled
        : undefined;
  return { automations: list, automationsEnabled: enabled };
};

export const deriveAutomationsEnabled = (res: AutomationsResponse, settings?: EngagementSettings): boolean | undefined => {
  if (typeof res.automationsEnabled === 'boolean') return res.automationsEnabled;
  if (typeof settings?.flags?.automationsEnabled === 'boolean') return settings.flags.automationsEnabled;
  const ran = res.automations.filter((a) => a.enabled && a.lastRunStats);
  if (ran.some((a) => (a.lastRunStats?.wouldSend ?? 0) > 0 && (a.lastRunStats?.sent ?? 0) === 0)) {
    // Something qualified and nothing went out on any enabled automation → switched off globally.
    return ran.every((a) => (a.lastRunStats?.sent ?? 0) === 0) ? false : undefined;
  }
  return undefined;
};
